import { useEffect } from 'react'

type ClampPageIntoRangeParams = {
  /** The page the URL asks for, 1-based. */
  page: number
  pageSize: number
  /** The server's count of matching rows across every page — NOT the rows in hand. */
  total: number
  /** A load is in flight, including a `keepPreviousData` window over the outgoing page. */
  loading: boolean
  /** Repair the URL. Receives the last page that actually holds rows, 1-based. */
  onClamp: (page: number) => void
}

/**
 * Pulls a page index that points past the end of the result set back into range.
 *
 * A shared link outlives the page count it was cut from, so any `?page=` can go stale —
 * the server answers it with no rows for a set that is NOT empty. DataGridPro clamped
 * such an index itself and emitted the correction; the DS pager only disables its nav, so
 * without this the admin sits on a blank page, and a page whose empty state reads `total`
 * would unmount the very control that could have paged them back.
 *
 * Deliberately narrow. It waits for a SETTLED count (mid-load, `total` still describes the
 * page being replaced) and ignores an empty result: total 0 is the empty state's business,
 * and clamping there would fight a filter the admin is still editing.
 */
export const useClampPageIntoRange = ({
  page,
  pageSize,
  total,
  loading,
  onClamp,
}: ClampPageIntoRangeParams) => {
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  const isPastEnd = !loading && total > 0 && page > lastPage

  useEffect(
    function clampPageIntoRange() {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- FE-892 tracks codifying this shape (FE-760 established the justified-disable route for this rule): async server-data arrival, not a user event: the page index comes from the URL and the count that invalidates it from the query response, so "the server has fewer pages than this link asks for" has no handler to live in — nobody clicked. DataGridPro owned this clamp internally and emitted the correction; the DS pager only disables its nav. The guard makes the call idempotent, and the caller's URL write is what ends the condition.
      if (!isPastEnd) {
        return
      }
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- FE-892, as above: the rule's fix (return `lastPage` instead) does not apply: the repair IS a navigation, so a returned value would only move this same effect into every caller. `lastPage` is derived from the caller's own params, not state this hook holds, and the write is what ends the condition.
      onClamp(lastPage)
    },
    [isPastEnd, lastPage, onClamp],
  )
}
